import React from 'react';
import { Box, Typography } from '@mui/material';
import { SentimentNeutral as EmptyIcon } from '@mui/icons-material';
import CustomPlayerImageWithDefault from '@/components/shared/custom/CustomPlayerImageWithDefault';
import { getPlayerIconWhenIsNotOk } from '@/helpers/players-helper';
import NoInscritoIcon from '@/components/shared/icons/recommendation-icons/no-inscrito-icon';
import { getCountryImage } from '@/helpers/images-helper';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import { countries } from '@/utils/country';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';

const JugadoresStatusCards = ({ jugadores }) => {
    const jugadoresEnDuda = jugadores?.filter((jugador) => jugador.status === 'doubt') || [];
    const jugadoresLesionados = jugadores?.filter((jugador) => jugador.status === 'injured') || [];
    const jugadoresSancionados = jugadores?.filter((jugador) => jugador.status === 'sanctioned') || [];
    const jugadoresNoInscritos = jugadores?.filter((jugador) => jugador.isNoInscrito) || [];
    const jugadoresConvocados = jugadores?.filter((jugador) => jugador.isConvocado) || [];

    const SeccionVacia = ({ titulo }) => (
        <CustomSectionWrapper title={titulo} sx={{ mb: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <EmptyIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                <Typography fontSize={12} color="text.secondary">
                    No hay jugadores para esta categoría
                </Typography>
            </Box>
        </CustomSectionWrapper>
    );

    const FilaJugador = ({ jugador, description }) => {
        const jugadorIcon = getPlayerIconWhenIsNotOk(jugador.status, jugador.statusLevel ?? 1, 12, 12);

        return (
            <Box
                sx={{
                    pb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'border.light',
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <CustomPlayerImageWithDefault
                        playerId={jugador.id}
                        width={50}
                        height={50}
                        alt={jugador.name}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            minWidth: 24,
                            height: 24,
                        }}
                    >
                        {jugadorIcon}
                    </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography fontSize={12}>{jugador.name}</Typography>
                    <Typography fontSize={12}>{jugador.report}</Typography>
                    <Typography fontSize={12} color="text.secondary">
                        {description ?? jugador.statusInfo}
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Componente para mostrar sección con jugadores
    const SeccionConJugadores = ({ titulo, jugadores }) => (
        <CustomSectionWrapper title={titulo} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {jugadores.map((jugador, index) => (
                    <FilaJugador key={jugador.id || index} jugador={jugador} />
                ))}
            </Box>
        </CustomSectionWrapper>
    );

    const SeccionConJugadoresConvocados = ({ titulo, jugadores }) => {
        return (
            <CustomSectionWrapper title={titulo} sx={{ mb: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    {jugadores.map((jugador, index) => {
                        const icon = getCountryImage(jugador.countryConvocado, 18);
                        const countryName = countries.find(
                            (country) => country.code === jugador.countryConvocado
                        )?.name;

                        const image = (
                            <CustomImageComponent
                                src={icon}
                                alt={countryName}
                                width={12}
                                height={12}
                                style={{ borderRadius: '50%', position: 'absolute', top: 5, left: 5 }}
                            />
                        );

                        return (
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flexDirection: 'column',
                                }}
                                key={`${jugador.id}-${index}`}
                            >
                                <CustomPlayerImageWithDefault
                                    playerId={jugador.id}
                                    width={70}
                                    height={70}
                                    alt={jugador.name}
                                />

                                {image}
                                <Typography fontSize={12}>{jugador.name}</Typography>
                            </Box>
                        );
                    })}
                </Box>
            </CustomSectionWrapper>
        );
    };

    return (
        <>
            {/* Sección Jugadores en Duda */}
            {jugadoresEnDuda.length > 0 ? (
                <SeccionConJugadores titulo="Jugadores en Duda" jugadores={jugadoresEnDuda} />
            ) : (
                <SeccionVacia titulo="Jugadores en Duda" />
            )}

            {/* Sección Jugadores Lesionados */}
            {jugadoresLesionados.length > 0 ? (
                <SeccionConJugadores titulo="Jugadores Lesionados" jugadores={jugadoresLesionados} />
            ) : (
                <SeccionVacia titulo="Jugadores Lesionados" />
            )}

            {/* Sección Jugadores Sancionados */}
            {jugadoresSancionados.length > 0 ? (
                <SeccionConJugadores titulo="Jugadores Sancionados" jugadores={jugadoresSancionados} />
            ) : (
                <SeccionVacia titulo="Jugadores Sancionados" />
            )}

            {jugadoresNoInscritos.length > 0 ? (
                <SeccionConJugadores
                    titulo="Jugadores No Inscritos"
                    jugadores={jugadoresNoInscritos}
                    icon={<NoInscritoIcon height={12} width={12} />}
                />
            ) : (
                <SeccionVacia titulo="Jugadores No Inscritos" />
            )}

            {jugadoresConvocados.length > 0 && (
                <SeccionConJugadoresConvocados
                    titulo="Jugadores Convocados"
                    jugadores={jugadoresConvocados}
                />
            )}
        </>
    );
};

export default JugadoresStatusCards;
